import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import DataTable from '../componentes/DataTable';
import EditModal from '../componentes/EditModal';
import DeleteConfirm from '../componentes/DeleteConfirm';
import QRModal from '../componentes/QRModal';
import SearchBar from '../componentes/SearchBar';
import { Plus, Download, QrCode, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const CodigosPanel = () => {
  const [codigos, setCodigos] = useState([]);
  const [mascotas, setMascotas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');
  const [filtroUso, setFiltroUso] = useState('todos'); // 'todos', 'usados', 'no-usados'
  const [codigoQR, setCodigoQR] = useState(null);
  const [showQR, setShowQR] = useState(false);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [resCodigos, resMascotas, resUsuarios] = await Promise.all([
        api.getCodigos(),
        api.getMascotas(),
        api.getUsuarios(),
      ]);

      setCodigos(resCodigos.data?.data || resCodigos.data || []);
      setMascotas(resMascotas.data?.data || resMascotas.data || []);
      setUsuarios(resUsuarios.data?.data || resUsuarios.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAllData(); }, []);

  // Enriquecer cada código con info de uso (mascota + dueño)
  const codigosEnriquecidos = useMemo(() => {
    const mascotaPorCodigo = {};
    mascotas.forEach(m => {
      if (m.codigo_id) mascotaPorCodigo[m.codigo_id] = m;
    });

    const usuarioPorId = {};
    usuarios.forEach(u => { usuarioPorId[u.id] = u; });

    return codigos.map(c => {
      const mascota = mascotaPorCodigo[c.id];
      const usado = !!mascota;
      let usuarioNombre = '—';
      if (usado && mascota.id_usuario) {
        const user = usuarioPorId[mascota.id_usuario];
        if (user) usuarioNombre = `${user.nombre} ${user.apellido}`.trim();
      }
      return {
        ...c,
        usado,
        usuario: usuarioNombre,
        mascotaId: mascota?.id || null,
        mascotaNombre: mascota?.nombre || '—',
      };
    });
  }, [codigos, mascotas, usuarios]);

  const filtrados = useMemo(() => {
    let result = codigosEnriquecidos;
    const q = search.trim().toLowerCase();
    if (q) result = result.filter(c => c.codigo_unico?.toLowerCase().includes(q));
    if (filtroUso === 'usados') result = result.filter(c => c.usado);
    else if (filtroUso === 'no-usados') result = result.filter(c => !c.usado);
    return result;
  }, [codigosEnriquecidos, search, filtroUso]);

  const stats = useMemo(() => ({
    total: codigosEnriquecidos.length,
    usados: codigosEnriquecidos.filter(c => c.usado).length,
    libres: codigosEnriquecidos.filter(c => !c.usado).length,
  }), [codigosEnriquecidos]);

  // ─── CRUD ───────────────────────────────────────────────
  const handleEdit = (codigo) => { setSelected({ ...codigo }); setShowEdit(true); };
  const handleCreate = () => { setSelected(null); setShowEdit(true); };
  const handleDelete = (id) => { setSelected(codigosEnriquecidos.find(c => c.id === id)); setShowDelete(true); };

  const handleSave = async (formData) => {
    try {
      if (selected) {
        await api.updateCodigo(selected.id, formData);
      } else {
        await api.createCodigo({ codigo_unico: formData.codigo_unico });
      }
      setShowEdit(false);
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteCodigo(selected.id);
      setShowDelete(false);
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  // ─── Exportar a Excel ──────────────────────────────────
  const exportarExcel = () => {
    const datos = filtrados.map(c => ({
      ID: c.id,
      Código: c.codigo_unico,
      Usado: c.usado ? 'Sí' : 'No',
      Mascota: c.mascotaNombre,
      Dueño: c.usuario,
    }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Códigos QR');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'codigos_qr.xlsx');
  };

  // ─── Abrir modal de QR (descarga/imprime/comparte/copia enlace) ───
  const handleGenerarQR = (codigo) => {
    setCodigoQR(codigo);
    setShowQR(true);
  };

  // ─── Columnas ────────────────────────────────────────────
  // IMPORTANTE: DataTable llama render(row[col.key], row) → firma (value, row)
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'codigo_unico', label: 'Código' },
    {
      key: 'usado',
      label: 'Estado',
      render: (value) => (value ? '✅ Usado' : '⭕ Libre'),
    },
    {
      key: 'mascotaNombre',
      label: 'Mascota',
      render: (value) => value,
    },
    {
      key: 'usuario',
      label: 'Dueño',
      render: (value) => value,
    },
    {
      id: 'qr',
      key: 'id',
      label: 'QR',
      render: (value, row) => (
        <button
          className="sp-btn sp-btn--primary"
          style={{ minHeight: 34, padding: '0.3rem 0.7rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          onClick={() => handleGenerarQR(row)}
          title="Generar imagen QR"
        >
          <QrCode size={15} /> Generar
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Códigos QR</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
            <Plus size={18} /> Nuevo
          </button>
          <button className="sp-btn sp-btn--light" onClick={exportarExcel} title="Exportar a Excel">
            <Download size={18} /> Excel
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', fontSize: '0.85rem', color: 'var(--sp-muted)' }}>
        <span>Total: <strong>{stats.total}</strong></span>
        <span>Usados: <strong>{stats.usados}</strong></span>
        <span>Libres: <strong>{stats.libres}</strong></span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Buscar código..." />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Filter size={18} />
          <select
            value={filtroUso}
            onChange={(e) => setFiltroUso(e.target.value)}
            style={{
              padding: '0.4rem 0.8rem',
              borderRadius: 'var(--sp-radius-sm)',
              border: '1px solid var(--sp-border)',
              background: 'var(--sp-bg)',
              fontSize: '0.9rem',
            }}
          >
            <option value="todos">Todos</option>
            <option value="usados">Usados</option>
            <option value="no-usados">No usados</option>
          </select>
        </div>
        <span style={{ fontSize: '0.85rem', color: 'var(--sp-muted)' }}>
          {filtrados.length} códigos
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filtrados}
        loading={loading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {showEdit && (
        <EditModal
          title={selected ? 'Editar código' : 'Crear código'}
          fields={[{ name: 'codigo_unico', label: 'Código único (formato AAAA0000)' }]}
          initialValues={selected || {}}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showQR && codigoQR && (
        <QRModal codigo={codigoQR} onClose={() => setShowQR(false)} />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar el código ${selected?.codigo_unico}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default CodigosPanel;